/**
 * Modal del Pozo Acumulado — cinco apartados sobre TabbedModal (SSOT).
 *
 * Solo HTML: el estado, la hidratación y el guardado viven en
 * `modules/pozos-comunes.js`, que ya usa Borrador + CONTRATO_POZO_COMUN.
 *
 * Vocabulario: «pozo acumulado», nunca «jackpot». Los enums internos
 * (`sesgada_al_techo`, `primer_apostador`…) no se muestran: viajan en el value.
 */
import { MonedaService } from '../../services/moneda-service.js';
import { getAvailableAnimationThemesForSelector } from '@shared/config/pozo-animation-themes.js';
import { t } from '../../services/i18n-admin.js';
import { icon } from '../../utils/icon.js';
import { TabbedModal } from '../tabbed-modal.js';

const ID_MODAL = 'modalPozoComun';

const APARTADOS = () => [
    { id: 'identidad', icon: 'tag', title: t('Identidad'), desc: t('Nombre, grupo y nivel') },
    { id: 'economia', icon: 'trending-up', title: t('Economía'), desc: t('Base y disparo') },
    { id: 'ciclo', icon: 'repeat', title: t('Ciclo y reserva'), desc: t('Reparto del aporte') },
    { id: 'presentacion', icon: 'monitor', title: t('Presentación'), desc: t('Lo que ve el jugador') },
    { id: 'estado', icon: 'activity', title: t('Estado'), desc: t('Uso y disponibilidad') },
];

function _panelIdentidad() {
    return `
    <div class="form-group" id="pozoNombreGrupo">
      <label class="form-label" for="pozoNombre">${t('Nombre')}</label>
      <input type="text" id="pozoNombre" class="form-input" maxlength="100" placeholder="${t('Ej: Pozo Millonario')}">
    </div>
    <div class="form-group" id="pozoDescripcionGrupo">
      <label class="form-label" for="pozoDescripcion">${t('Descripción (opcional)')}</label>
      <input type="text" id="pozoDescripcion" class="form-input" maxlength="500" placeholder="${t('Para qué se usa este pozo')}">
    </div>

    <div class="form-group">
      <label class="form-label" for="pozoGrupo">${t('Grupo')}</label>
      <input type="text" id="pozoGrupo" class="form-input" list="pozoGruposExistentes"
             placeholder="${t('Ej: Lima')}" maxlength="60" aria-describedby="pozoGrupoAyuda">
      <datalist id="pozoGruposExistentes"></datalist>
      <div class="form-hint" id="pozoGrupoAyuda">${t('Junta los cuatro pozos de una colección para elegirlos de una vez. Es solo una etiqueta: no mueve dinero.')}</div>
      <label class="form-label" id="pozoCrearGrupoCompletoGrupo"
             style="display:none; align-items:center; gap:8px; margin-top:10px;">
        <input type="checkbox" id="pozoCrearGrupoCompleto" style="width:auto; margin:0;">
        ${t('Crear los cuatro pozos acumulados de este grupo')}
      </label>
    </div>

    <div class="form-group" id="pozoNivelGrupo">
      <label class="form-label" for="pozoNivel">${t('Nivel')}</label>
      <select id="pozoNivel" class="form-select" aria-describedby="pozoNivelAyuda">
        <option value="">${t('Sin nivel (configuración anterior)')}</option>
        <option value="grand">Grand</option>
        <option value="major">Major</option>
        <option value="minor">Minor</option>
        <option value="mini">Mini</option>
      </select>
      <div class="form-hint" id="pozoNivelAyuda">${t('Grand, Major, Minor y Mini son los cuatro niveles de una colección. Cada pozo es exactamente uno; Mini no sobra ni es un quinto elemento.')}</div>
    </div>

    <div class="form-row">
      <div class="form-group">
        <label class="form-label" for="pozoMoneda">${t('Moneda')}</label>
        <select id="pozoMoneda" class="form-select">${MonedaService.buildSelectorOptions()}</select>
      </div>
      <div class="form-group" id="pozoAmbitoGrupo">
        <label class="form-label" for="pozoAmbito">${t('Ámbito')}</label>
        <input type="text" id="pozoAmbito" class="form-input" readonly disabled>
        <div class="form-hint">${t('Quién es el propietario. Dos grupos con el mismo nombre en ámbitos distintos son grupos distintos.')}</div>
      </div>
    </div>

    <div id="pozoPreviewDelGrupo" style="display:none;">
      <label class="form-label">${t('Se van a crear estos cuatro pozos acumulados')}</label>
      <div id="pozoPreviewDelGrupoFilas" class="pozo-preview-grupo"></div>
      <div class="form-hint">${t('Quedan disponibles para elegirlos, con la base económica de abajo. No se financian y no activan ningún producto.')}</div>
    </div>`;
}

function _panelEconomia() {
    // Todo el apartado salvo la moneda es del pozo INDIVIDUAL: en el alta del
    // grupo lo fija el preset por nivel, así que se oculta en ese modo.
    return `
    <div id="pozoEconomiaIndividual">
    <div class="form-row">
      <div class="form-group">
        <label class="form-label" for="pozoPozoBase">${t('Base de reinicio')}</label>
        <div class="input-con-sufijo">
          <input type="text" id="pozoPozoBase" class="form-input" inputmode="decimal" aria-describedby="pozoPozoBaseAyuda">
          <span class="input-sufijo" id="pozoPozoBaseMoneda"></span>
        </div>
        <div class="form-hint" id="pozoPozoBaseAyuda">${t('Importe al que vuelve el pozo después de pagarse.')}</div>
      </div>
      <div class="form-group">
        <label class="form-label" for="pozoPctAporteDefecto">${t('Contribución total por apuesta')}</label>
        <input type="number" id="pozoPctAporteDefecto" class="form-input" min="0.01" max="100" step="0.01"
               aria-describedby="pozoAporteAyuda">
        <div class="form-hint" id="pozoAporteAyuda">${t('Porcentaje de cada apuesta que va a este pozo.')}</div>
      </div>
    </div>

    <label class="form-label">${t('Rango de disparo')}</label>
    <div class="form-row">
      <div class="form-group">
        <label class="form-label form-label-sutil" for="pozoMinMult">${t('Desde')}</label>
        <input type="number" id="pozoMinMult" class="form-input" min="1" max="100" step="0.1">
      </div>
      <div class="form-group">
        <label class="form-label form-label-sutil" for="pozoMaxMult">${t('Hasta')}</label>
        <input type="number" id="pozoMaxMult" class="form-input" min="1" max="1000" step="0.1">
      </div>
    </div>
    <div class="form-hint" id="pozoVistaPreviaDisparo" aria-live="polite"></div>

    <div class="form-group" id="pozoSeleccionGrupo" style="margin-top:14px;">
      <label class="form-label" for="pozoModoSeleccion">${t('Cómo se elige al ganador')}</label>
      <select id="pozoModoSeleccion" class="form-select" aria-describedby="pozoModoAyuda">
        <option value="ponderado">${t('Ponderada')}</option>
        <option value="uniforme">${t('Uniforme')}</option>
        <option value="primer_apostador">${t('Primer apostador')}</option>
        <option value="ultimo_apostador">${t('Último apostador')}</option>
      </select>
      <div class="form-hint" id="pozoModoAyuda"></div>
    </div>

    <div class="form-group" id="pozoPresetGrupo">
      <button type="button" class="btn btn-sm btn-secondary" id="pozoAplicarPresetBtn"
              data-action="pozos:aplicarPresetDeReferencia">${t('Cargar valores de referencia')}</button>
      <div class="form-hint">${t('Rellena base, rango, contribución y reparto con la configuración aprobada para el nivel elegido. Solo cambia el formulario: nada se guarda hasta pulsar Guardar.')}</div>
    </div>
    </div>`;
}

function _panelCicloYReserva() {
    // Conservar la fraccion por debajo de la unidad monetaria NO se configura:
    // es contabilidad correcta y aplica a todos los pozos. Aqui habia una
    // casilla; se retiro con el acarreo universal. El reparto y el umbral si
    // son de cada pozo y los fija el preset.
    return `
    <div id="pozoCicloIndividual">
    <div class="form-row">
      <div class="form-group">
        <label class="form-label" for="pozoPctAporteVisible">${t('Parte que aumenta el pozo visible')}</label>
        <input type="number" id="pozoPctAporteVisible" class="form-input" min="0" max="100" step="0.01">
      </div>
      <div class="form-group">
        <label class="form-label" for="pozoPctAporteReserva">${t('Parte reservada para financiar el siguiente ciclo')}</label>
        <input type="number" id="pozoPctAporteReserva" class="form-input" min="0" max="100" step="0.01">
      </div>
    </div>

    <div class="form-row">
      <div class="form-group">
        <label class="form-label" for="pozoEstrategiaDistribucion">${t('Distribución del umbral')}</label>
        <select id="pozoEstrategiaDistribucion" class="form-select">
          <option value="uniforme">${t('Pareja en todo el rango')}</option>
          <option value="sesgada_al_techo">${t('Tendencia hacia importes mayores')}</option>
        </select>
      </div>
      <div class="form-group" id="pozoSesgoGrupo" style="display:none;">
        <label class="form-label" for="pozoSesgoDistribucion">${t('Fuerza de la tendencia')}</label>
        <input type="number" id="pozoSesgoDistribucion" class="form-input" min="1" max="20" step="0.5">
        <div class="form-hint">${t('1 = sin tendencia.')}</div>
      </div>
    </div>

    <div class="resumen-calculado" id="pozoResumenDelCiclo" aria-live="polite"></div>

    <div class="form-row" id="pozoSaldosInternos" style="display:none;">
      <div class="form-group">
        <label class="form-label" for="pozoReservaActual">${t('Reserva actual')}</label>
        <input type="text" id="pozoReservaActual" class="form-input" readonly disabled>
      </div>
      <div class="form-group">
        <label class="form-label" for="pozoOverflowActual">${t('Excedente actual')}</label>
        <input type="text" id="pozoOverflowActual" class="form-input" readonly disabled>
      </div>
    </div>
    </div>`;
}

function _panelPresentacion() {
    const temas = getAvailableAnimationThemesForSelector();
    const selectorDeTema =
        temas.length <= 1
            ? ''
            : `
    <div class="form-group">
      <label class="form-label" for="pozoAnimationTheme">${t('Tema de animación')}</label>
      <select id="pozoAnimationTheme" class="form-select">
        ${temas.map(tema => `<option value="${tema.id}">${tema.name}</option>`).join('')}
      </select>
      <div class="form-hint">${t('Estilo visual de la animación cuando el pozo se paga.')}</div>
    </div>`;

    return `
    <div id="pozoPresentacionIndividual">
    <div class="form-group">
      <label class="form-label" for="pozoDisplayTitle">${t('Título visible')}</label>
      <input type="text" id="pozoDisplayTitle" class="form-input" maxlength="100" placeholder="${t('Ej: POZO MAYOR')}">
      <div class="form-hint">${t('Lo que se muestra en pantalla. Si se deja vacío, se usa el nombre.')}</div>
    </div>
    ${selectorDeTema}
    </div>`;
}

function _panelEstado() {
    return `
    <div class="form-group">
      <label class="form-label" style="display:flex; align-items:center; gap:8px;">
        <input type="checkbox" id="pozoActivo" style="width:auto; margin:0;" aria-describedby="pozoActivoAyuda">
        ${t('Disponible para selección')}
      </label>
      <div class="form-hint" id="pozoActivoAyuda">${t('Disponible significa que se puede elegir. No significa que esté financiado, ni que haya un producto activo usándolo.')}</div>
    </div>

    <div class="estado-lineas" id="pozoEstadoLineas"></div>

    <div class="danger-zone" id="pozoDangerZone" style="display:none;">
      <label class="form-label form-label-peligro">${t('Zona de peligro')}</label>
      <div class="form-hint">${t('Estas acciones afectan al dinero acumulado o retiran el pozo de circulación.')}</div>
      <div style="display:flex; gap:8px; flex-wrap:wrap; margin-top:8px;">
        <button class="btn btn-sm btn-precaucion" id="pozoResetBtn"
                data-action="pozos:saveAndResetPozo">${t('Guardar y reiniciar el acumulado')}</button>
        <button class="btn btn-danger btn-sm" data-action="pozos:deletePozo">${t('Eliminar')}</button>
      </div>
    </div>`;
}

/** Badge + detalles plegables: sustituye al aviso naranja de cuatro líneas. */
function _avisoDeConfiguracionAnterior() {
    return `
    <details id="pozoAvisoAnterior" class="aviso-configuracion-anterior" style="display:none;">
      <summary>
        <span class="config-badge badge-anterior">${t('Configuración anterior')}</span>
        <span class="aviso-resumen">${t('Sigue operando igual. Se puede editar sin convertirlo.')}</span>
      </summary>
      <div class="aviso-detalle">
        <p>${t('Este pozo se creó antes de los cuatro niveles: no tiene nivel asignado y todo su aporte va al importe visible.')}</p>
        <p>${t('Abrirlo, editar su nombre o guardar no lo convierte. Conserva su id, su saldo, su base, su historial y sus suscripciones.')}</p>
        <button type="button" class="btn btn-sm btn-secondary" id="pozoConvertirBtn"
                data-action="pozos:iniciarConversionAlModeloDeNiveles">${t('Completar la configuración de niveles')}</button>
      </div>
    </details>`;
}

export function pozoAcumuladoModal() {
    const tabs = APARTADOS();
    const panels = {
        identidad: _panelIdentidad(),
        economia: _panelEconomia(),
        ciclo: _panelCicloYReserva(),
        presentacion: _panelPresentacion(),
        estado: _panelEstado(),
    };

    return `
    <div class="modal" id="${ID_MODAL}" style="display:none; max-width:860px;"
         role="dialog" aria-modal="true" aria-labelledby="modalPozoComunTitle">
      <div class="modal-header">
        <div>
          <h2 id="modalPozoComunTitle">${t('Nuevo pozo acumulado')}</h2>
          ${_avisoDeConfiguracionAnterior()}
        </div>
        <button class="btn-icon" data-action="app:closeModal" aria-label="${t('Cerrar')}">${icon('x')}</button>
      </div>
      <div class="modal-body">
        ${TabbedModal.render({ tabs, panels, defaultTab: 'identidad', modalId: ID_MODAL })}
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" data-action="app:closeModal">${t('Cancelar')}</button>
        <button class="btn btn-primary" id="pozoSaveBtn" data-action="pozos:saveModal">${t('Guardar')}</button>
      </div>
    </div>`;
}
